import React, { useEffect, useRef, useState } from 'react';
import { Modal, Alert, Typography } from 'antd';
import jsQR from 'jsqr';
import { decodeCart } from '../../utils/cartShare';

const { Text } = Typography;

/*
ImportListQRScanner
Props:
 - open: boolean
 - onClose(): void
 - onDecoded(code: string): void  (passes raw code segment, not URL)
*/
export default function ImportListQRScanner({ open, onClose, onDecoded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { stop(); return; }

    let frameReq;
    const start = async () => {
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          loop();
        }
      } catch (e) {
        setError('Camera access denied or unavailable');
      }
    };

    const loop = () => {
      if (!videoRef.current || videoRef.current.readyState !== 4) {
        frameReq = requestAnimationFrame(loop);
        return;
      }
      const w = videoRef.current.videoWidth;
      const h = videoRef.current.videoHeight;
      if (!w || !h) { frameReq = requestAnimationFrame(loop); return; }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = w; canvas.height = h;
      ctx.drawImage(videoRef.current, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const qr = jsQR(imageData.data, w, h);
      if (qr && qr.data) {
        const raw = qr.data.trim();
        // validate quickly
        decodeCart(raw);
        stop();
        onDecoded(raw);
        onClose();
        return;
      }
      frameReq = requestAnimationFrame(loop);
    };

    start();
    return () => cancelAnimationFrame(frameReq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stop = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  return (
    <Modal open={open} onCancel={() => { stop(); onClose(); }} footer={null} title="Scan Shared List" destroyOnClose>
      {error && <Alert type='error' message={error} style={{ marginBottom: 12 }} />}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline muted />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(255,255,255,0.4)', pointerEvents: 'none' }} />
      </div>
      <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        Align the QR code inside the frame. The list will import automatically.
      </Text>
    </Modal>
  );
}
